/**
 * RichContentPage.jsx — Phase 2 (Content Enrichment) D7
 *
 * Marketing-style rich page for Services value pillars (PowerCarbide,
 * Sustainability, Carbide recycling, Centrevo digital suite + its 4
 * sub-applications, Our Way). Does NOT render a product list — Services
 * value pillars are Sandvik's differentiators, not shop-and-order items.
 *
 * Content hydrates from content-overlay.json via getContentForSlug().
 * Every rich page surfaces a prominent "Read more on Sandvik.com ↗" CTA
 * built from the crawl `sourceUrl` — this is a UX + SEO pillar (Vandana,
 * 2026-07-08).
 *
 * @module pages/RichContentPage
 * @since 2026-07-08 (feat/content-enrichment)
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, Mail } from 'lucide-react';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import { useContactModal } from '@contexts/ContactModalContext';
import { loadContentOverlay, getContentForSlug } from '@data/contentOverlay';

function useOverlay(slug) {
  const [content, setContent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadContentOverlay().then(() => {
      if (!cancelled) setContent(getContentForSlug(slug));
    });
    return () => { cancelled = true; };
  }, [slug]);
  return content;
}

/**
 * @param {object} props
 * @param {object} props.productLineData — nav product line node
 * @param {object} props.node — the category or subcategory node being rendered
 * @param {Array}  [props.breadcrumbs] — optional pre-built breadcrumbs
 */
export default function RichContentPage({ productLineData, node, breadcrumbs }) {
  const { openContact } = useContactModal();
  const content = useOverlay(node?.id);

  const heroImage = content?.heroImage;
  const description = content?.description || node?.description;
  const heroText = node?.heroText || content?.title || node?.title;
  const features = content?.features || [];
  const detailedInfo = content?.detailedInfo;
  const downloads = content?.downloads || [];
  const sourceUrl = content?.sourceUrl;
  const subcategories = node?.subcategories || [];

  const crumbs = breadcrumbs || [
    { label: 'Products', to: '/products' },
    { label: productLineData?.shortTitle || productLineData?.title, to: productLineData?.route },
    { label: node?.shortTitle || node?.title, to: null, isLast: true },
  ];

  return (
    <div>
      {/* Hero band — hero image renders md+ only; mobile gets the gradient
          alone for a lighter, faster page. */}
      <div className="relative bg-gradient-to-r from-carbon-black to-gray-800">
        {heroImage && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url("${heroImage}")` }}
          />
        )}
        <div className="sandvik-container py-14 md:py-20 relative z-10">
          <Breadcrumbs items={crumbs} className="[&_*]:!text-white/60 mb-3" />
          <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl text-white mb-2">
            {node?.title}
          </h1>
          {heroText && heroText !== node?.title && (
            <p className="text-sandvik-gold font-sandvik-text text-lg mb-4">
              {heroText}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 hover:bg-white/20 transition-colors text-sm font-sandvik-text"
            >
              Read more on Sandvik.com
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      <div className="sandvik-container py-10">
        {description && (
          <div className="max-w-3xl mb-10">
            <p className="text-n7 font-sandvik-text leading-relaxed text-lg">
              {description}
            </p>
          </div>
        )}

        {/* Sub-application tiles (Centrevo: Track / Drill / Analyze / Report) */}
        {subcategories.length > 0 && (
          <div className="mb-12">
            <h2 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-5">
              Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {subcategories.map((sub) => (
                <Link
                  key={sub.id}
                  to={sub.route}
                  className="group block border border-n2 bg-white p-5 hover:border-electric-blue hover:shadow-md transition-all"
                >
                  <h3 className="font-sandvik-headline font-normal text-lg text-carbon-black group-hover:text-electric-blue mb-2">
                    {sub.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-electric-blue text-sm font-sandvik-text">
                    Learn more
                    <ChevronRight size={16} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Features & Details — card grid */}
        {features.length > 0 && (
          <div className="mb-12">
            <h2 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-5">
              Features & Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((f, i) => (
                <div key={i} className="border-l-4 border-sandvik-gold bg-n1/40 p-5">
                  <h3 className="font-sandvik-text font-medium text-carbon-black mb-2">
                    {f.title}
                  </h3>
                  {f.body && (
                    <p className="text-n7 font-sandvik-text text-sm leading-relaxed">
                      {f.body}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Information — collapsed by default */}
        {detailedInfo && (
          <details className="mb-12 border border-n2 bg-n1/30">
            <summary className="cursor-pointer px-5 py-4 font-sandvik-text font-medium text-carbon-black hover:bg-n1">
              Learn more
            </summary>
            <div className="px-5 pb-5 pt-1 text-n7 font-sandvik-text leading-relaxed text-sm">
              {detailedInfo}
            </div>
          </details>
        )}

        {/* Downloads panel */}
        {downloads.length > 0 && (
          <div className="mb-12">
            <h2 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-5">
              Downloads
            </h2>
            <ul className="space-y-2">
              {downloads.map((dl, i) => (
                <li key={i}>
                  <a
                    href={dl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-electric-blue font-sandvik-text hover:underline"
                  >
                    <ExternalLink size={16} />
                    {dl.title}
                    {dl.sizeLabel && <span className="text-n5 text-sm">({dl.sizeLabel})</span>}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contact CTA */}
        <div className="border-t border-n2 pt-8 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => openContact('product-info', { productName: node?.title })}
            className="inline-flex items-center gap-2 bg-electric-blue text-white px-6 py-3 font-sandvik-text text-sm hover:bg-electric-blue/90 transition-colors"
          >
            <Mail size={16} />
            Get in touch
          </button>
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-electric-blue font-sandvik-text text-sm hover:underline"
            >
              Read more on Sandvik.com
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
